import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES, routeConfiguration } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  CustomTopbarButton,
  ExternalLink,
  NotificationBadge,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';



const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink className={css.topbarLink} name="LoginPage">
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

// Navigation links for Find Your Kind
const NavigationLinks = ({ currentPage }) => {
  return (
    <div className={css.navigationLinks}>
      <ExternalLink 
        href="https://findyourkind.com/"
        className={classNames(css.navLink, currentPage === 'LandingPage' ? css.currentPage : null)}
        target="_self"
      >
        <span className={css.navLinkLabel}>Home</span>
      </ExternalLink>
      <ExternalLink 
        href="https://findyourkind.com/#about"
        className={css.navLink}
        target="_self"
      >
        <span className={css.navLinkLabel}>About</span>
      </ExternalLink>
      <ExternalLink 
        href="https://findyourkind.com/#how-it-works"
        className={css.navLink}
        target="_self"
      >
        <span className={css.navLinkLabel}>How it works</span>
      </ExternalLink>
      <ExternalLink 
        href="https://findyourkind.com/#contact"
        className={css.navLink}
        target="_self"
      >
        <span className={css.navLinkLabel}>Contact us</span>
      </ExternalLink>
      <ExternalLink
        href="https://app.findyourkind.com/s"
        className={css.navLink}
        target="_self"
      >
        <span className={css.navLinkLabel}>Redeem voucher</span>
      </ExternalLink>
    </div>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout, showManageListingsLink, notificationCount, inboxTab }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  // Check if user is super
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  const isSuperUser = userType === 'super';

  const notificationBadge = notificationCount > 0 ? (
    <NotificationBadge className={css.notificationBadge} count={notificationCount} />
  ) : null;

  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {/* Inbox link - always visible for authenticated users */}
        <MenuItem key="InboxPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass(`InboxPage:${inboxTab}`))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.inbox" />
            {notificationBadge}
          </NamedLink>
        </MenuItem>
        {/* My Services — provider-main only */}
        {userType === 'provider-main' ? (
          <MenuItem key="ProviderServicesPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ProviderServicesPage'))}
              name="ProviderServicesPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.myServicesLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        {/* Manage Listings — super users (classic listing UI) */}
        {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        {/* Provider Manager link - only for super users */}
        {isSuperUser && (
          <MenuItem key="providerManager">
            <NamedLink 
              className={classNames(css.menuLink, currentPageClass('AdminPage'))}
              name="AdminPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.providerManagerLink" />
            </NamedLink>
          </MenuItem>
        )}
        {isSuperUser && (
          <MenuItem key="dashboard">
            <NamedLink 
              className={classNames(css.menuLink, currentPageClass('DashboardPage'))}
              name="DashboardPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.dashboardLink" />
            </NamedLink>
          </MenuItem>
        )}
        {isSuperUser && (
          <MenuItem key="features">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('FeaturesPage'))}
              name="FeaturesPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.featuresLink" />
            </NamedLink>
          </MenuItem>
        )}
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="TrackBalancePage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('TrackBalancePage'))}
            name="TrackBalancePage"
          >
            <span className={css.menuItemBorder} />
            My Gift Cards
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
    history,
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
      notificationCount={notificationCount}
      inboxTab={inboxTab}
    />
  ) : isAuthenticated ? (
    // Also show ProfileMenu for authenticated users even if not yet mounted (SSR compatibility)
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
      notificationCount={notificationCount}
      inboxTab={inboxTab}
    />
  ) : null;

  // const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const signupLinkMaybe = null; // Temporarily disabled
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  // Join the crew button (prominent secondary button)
  const joinCrewButton = (
    <ExternalLink 
      href="https://findyourkind.com/help-support/become-a-crew-member"
      className={css.joinCrewButton}
      target="_self"
    >
      Join the crew
    </ExternalLink>
  );

  // Buy Voucher button (primary CTA)
  const buyVoucherButton = (
    <CustomTopbarButton
      onClick={() => {
        history.push('/purchase');
      }}
      label="Buy Voucher"
      className={css.buyVoucherButton}
    />
  );
  

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );

  return (
    <nav className={classes}>
      <div className={css.navContainer}>
        <LinkedLogo
          className={css.logoLink}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
        
        <NavigationLinks currentPage={currentPage} />

        <div className={css.actionButtons}>
          <CustomLinksMenu
            currentPage={currentPage}
            customLinks={customLinks}
            intl={intl}
            hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
            showCreateListingsLink={showCreateListingsLink}
          />

          {joinCrewButton}
          {buyVoucherButton}
          {profileMenuMaybe}
          {signupLinkMaybe}
          {loginLinkMaybe}
        </div>
      </div>
    </nav>
  );
};

export default TopbarDesktop;
